import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { API_URL } from "../config/constants";
import { compressImage } from "../utils/imageCompression";
import "../assets/css/AdminInventory.css";

const STORAGE_KEY = "admin_inventory_token";

const initialFormState = {
  name: "",
  status: "Available",
  country: "UAE",
  regionalSpec: "",
  price: "",
  specs: ""
};
const statusOptions = ["Available", "Reserved", "Sold"];
const countryOptions = ["UAE", "India"];

const AdminInventory = () => {
  const [tokenInput, setTokenInput] = useState("");
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEY) || "");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(initialFormState);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);

  const isLoggedIn = useMemo(() => Boolean(token), [token]);

  const fetchInventory = async () => {
    const response = await fetch(`${API_URL}/inventory`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Failed to fetch inventory");
    setItems(Array.isArray(data.items) ? data.items : []);
  };

  useEffect(() => {
    fetchInventory().catch(() => {
      toast.error("Could not load inventory");
    });
  }, []);

  const parseSpecs = (specsText) =>
    specsText
      .split(",")
      .map((spec) => spec.trim())
      .filter(Boolean);

  const resetForm = () => {
    setForm(initialFormState);
    setEditingId("");
    setExistingImages([]);
    setNewImages([]);
  };

  const onLogin = async (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;

    try {
      const response = await fetch(`${API_URL}/inventory/admin/verify`, {
        method: "GET",
        headers: {
          "x-admin-token": tokenInput.trim()
        }
      });

      if (response.status === 401) {
        toast.error("Invalid admin token");
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Token check failed");
      }

      localStorage.setItem(STORAGE_KEY, tokenInput.trim());
      setToken(tokenInput.trim());
      toast.success("Admin access granted");
    } catch (error) {
      toast.error(error.message || "Login failed");
    }
  };

  const onLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setTokenInput("");
    resetForm();
  };

  const onSaveInventory = async () => {
    try {
      const response = await fetch(`${API_URL}/inventory/admin/save`, {
        method: "POST",
        headers: { "x-admin-token": token }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to save inventory");
      }
      toast.success("Inventory saved to disk");
    } catch (error) {
      toast.error(error.message || "Failed to save inventory");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const specs = parseSpecs(form.specs);
    const trimmedExistingImages = existingImages.filter(Boolean);

    if (
      !form.name.trim() ||
      !form.status.trim() ||
      !form.price.trim() ||
      specs.length === 0
    ) {
      toast.error("Fill all fields and at least one spec");
      return;
    }

    if (trimmedExistingImages.length === 0 && newImages.length === 0) {
      toast.error("Please add at least one image");
      return;
    }

    const formData = new FormData();
    formData.append("name", form.name.trim());
    formData.append("status", form.status.trim());
    formData.append("country", form.country);
    formData.append("regionalSpec", form.regionalSpec.trim());
    formData.append("price", form.price.trim());
    formData.append("specs", JSON.stringify(specs));
    formData.append("existingImages", JSON.stringify(trimmedExistingImages));
    newImages.forEach((file) => formData.append("images", file));

    setLoading(true);
    try {
      const response = await fetch(
        editingId ? `${API_URL}/inventory/${editingId}` : `${API_URL}/inventory`,
        {
          method: editingId ? "PUT" : "POST",
          headers: {
            "x-admin-token": token
          },
          body: formData
        }
      );

      const isJson = response.headers.get("content-type")?.includes("application/json");
      let data;
      if (isJson) {
        data = await response.json();
      } else {
        throw new Error(`Server error (${response.status}). If uploading many images, they might be too large.`);
      }

      if (!response.ok) {
        throw new Error(data?.message || "Failed to save inventory item");
      }

      toast.success(editingId ? "Inventory item updated" : "Inventory item added");
      resetForm();
      await fetchInventory();
    } catch (error) {
      toast.error(error.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      status: item.status,
      country: item.country || "UAE",
      regionalSpec: item.regionalSpec || "",
      price: item.price,
      specs: (item.specs || []).join(", ")
    });
    setExistingImages(item.images || []);
    setNewImages([]);
  };

  const onSelectImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const toastId = toast.loading("Compressing images...");
    try {
      const compressedFiles = await Promise.all(
        files.map((file) => compressImage(file))
      );
      setNewImages((prev) => [...prev, ...compressedFiles]);
      toast.success("Images added successfully", { id: toastId });
    } catch (err) {
      toast.error("Failed to process images", { id: toastId });
    }
    e.target.value = "";
  };

  const removeExistingImage = (imageUrl) => {
    setExistingImages((prev) => prev.filter((img) => img !== imageUrl));
  };

  const removeNewImage = (fileName) => {
    setNewImages((prev) => prev.filter((file) => file.name !== fileName));
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this inventory item?")) return;

    try {
      const response = await fetch(`${API_URL}/inventory/${id}`, {
        method: "DELETE",
        headers: { "x-admin-token": token }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to delete item");
      }
      toast.success("Inventory item deleted");
      await fetchInventory();
    } catch (error) {
      toast.error(error.message || "Failed to delete");
    }
  };

  const moveItem = async (index, direction) => {
    if (
      (direction === -1 && index === 0) ||
      (direction === 1 && index === items.length - 1)
    ) {
      return;
    }

    const newItems = [...items];
    const temp = newItems[index];
    newItems[index] = newItems[index + direction];
    newItems[index + direction] = temp;
    setItems(newItems);

    try {
      const orderedIds = newItems.map((item) => item.id);
      const response = await fetch(`${API_URL}/inventory/admin/reorder`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-admin-token": token
        },
        body: JSON.stringify({ orderedIds })
      });
      
      if (!response.ok) {
        throw new Error("Failed to save new order");
      }
    } catch (error) {
      toast.error(error.message || "Failed to reorder");
      await fetchInventory(); // Revert back
    }
  };

  if (!isLoggedIn) {
    return (
      <section className="admin-auth">
        <form className="auth-card" onSubmit={onLogin}>
          <h2>Admin Inventory</h2>
          <p className="admin-help">
            Enter your admin token to manage inventory.
          </p>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Admin token"
          />
          <button type="submit" className="btn c-btn w-100">
            Login
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="admin-inventory-page">
      <div className="container py-5">
        <div className="admin-header-row">
          <h1>Inventory Admin</h1>
          <div className="admin-header-actions">
            <button type="button" className="btn btn-success" onClick={onSaveInventory}>
              Save Inventory
            </button>
            <button type="button" className="btn btn-outline-dark" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>

        <div className="admin-card mb-4">
          <h4>{editingId ? "Edit Car" : "Add New Car"}</h4>
          <form onSubmit={onSubmit} className="row g-3 mt-1">
            <div className="col-md-4">
              <input
                className="form-control"
                placeholder="Car Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
                {!statusOptions.includes(form.status) && form.status && (
                  <option value={form.status}>{form.status}</option>
                )}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
              >
                {countryOptions.map((country) => (
                  <option key={country} value={country}>
                    {country.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                placeholder="Regional Spec (e.g. GCC)"
                value={form.regionalSpec}
                onChange={(e) => setForm({ ...form, regionalSpec: e.target.value })}
              />
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                placeholder="Price (e.g. INR 12.8 Lakh)"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </div>
            <div className="col-12">
              <input
                className="form-control"
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                multiple
                onChange={onSelectImages}
              />
            </div>
            {(existingImages.length > 0 || newImages.length > 0) && (
              <div className="col-12">
                <div className="admin-image-grid">
                  {existingImages.map((imageUrl, index) => (
                    <div className="admin-image-card" key={`${imageUrl}-${index}`}>
                      <img src={imageUrl} alt="Car" className="admin-car-thumb" />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger mt-2"
                        onClick={() => removeExistingImage(imageUrl)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}

                  {newImages.map((file) => (
                    <div className="admin-image-card" key={file.name}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="admin-car-thumb"
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger mt-2"
                        onClick={() => removeNewImage(file.name)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="col-12">
              <input
                className="form-control"
                placeholder="Specs separated by comma (e.g. Petrol, Automatic, 32,000 km)"
                value={form.specs}
                onChange={(e) => setForm({ ...form, specs: e.target.value })}
              />
            </div>
            <div className="col-12 d-flex gap-2">
              <button type="submit" className="btn c-btn" disabled={loading}>
                {loading ? "Saving..." : editingId ? "Update Car" : "Add Car"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={resetForm}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="admin-card">
          <h4>Current Inventory</h4>
          <div className="table-responsive mt-3">
            <table className="table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Car</th>
                  <th>Status</th>
                  <th>Regional Spec</th>
                  <th>Price</th>
                  <th>Specs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={item.id}>
                    <td>
                      <img
                        src={
                          (item.images && item.images[0]) ||
                          "https://placehold.co/180x110/e5e7eb/111827?text=No+Image"
                        }
                        alt={item.name}
                        className="admin-car-thumb"
                        onError={(e) => {
                          e.currentTarget.src =
                            "https://placehold.co/180x110/e5e7eb/111827?text=No+Image";
                        }}
                      />
                    </td>
                    <td>{item.name}</td>
                    <td>{item.status}</td>
                    <td>{item.regionalSpec || "-"}</td>
                    <td>{item.price}</td>
                    <td>{(item.specs || []).join(", ")}</td>
                    <td className="d-flex gap-2 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => moveItem(idx, -1)}
                        disabled={idx === 0}
                        title="Move Up"
                      >
                        ⬆️
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => moveItem(idx, 1)}
                        disabled={idx === items.length - 1}
                        title="Move Down"
                      >
                        ⬇️
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onEdit(item)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => onDelete(item.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminInventory;
